import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { ChevronDown, Check, Loader, Server, Mail } from 'lucide-react';
import { MailboxConnection } from '../../types';
import { workspaceHeader } from '../../lib/workspaces';

interface Sender {
  id: string;
  email: string;
  name: string;
}

interface Props {
  value: string; // currently selected sender email or 'mailbox:<mailboxId>'
  onChange: (email: string, mailboxId?: string) => void;
  className?: string;
}

async function getIdToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export default function SenderDropdown({ value, onChange, className = '' }: Props) {
  const [senders, setSenders] = useState<Sender[]>([]);
  const [mailboxes, setMailboxes] = useState<MailboxConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchAllSenders = useCallback(async () => {
    try {
      const token = await getIdToken();
      const [resSenders, resMailboxes] = await Promise.all([
        fetch('/api/email-senders', { headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() } }),
        fetch('/api/mailboxes', { headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() } }),
      ]);
      if (resSenders.ok) setSenders(await resSenders.json());
      if (resMailboxes.ok) setMailboxes(await resMailboxes.json());
    } catch (err) {
      console.error('Failed to fetch senders/mailboxes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllSenders(); }, [fetchAllSenders]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Determine display label
  const isMailboxSelect = value.startsWith('mailbox:');
  const mailboxId = isMailboxSelect ? value.replace('mailbox:', '') : undefined;
  const selectedMailbox = mailboxes.find(m => m.id === mailboxId || m.email === value);
  const selectedSender = senders.find(s => s.email === value);

  let display = 'Default Email Address';
  if (selectedMailbox) {
    display = `${selectedMailbox.displayName || selectedMailbox.email} <${selectedMailbox.email}>`;
  } else if (selectedSender) {
    display = `${selectedSender.name || selectedSender.email} <${selectedSender.email}>`;
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400 transition-all cursor-pointer"
      >
        <span className="truncate flex items-center space-x-1.5">
          {selectedMailbox ? (
            <Server className="h-3.5 w-3.5 text-brand shrink-0" />
          ) : (
            <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          )}
          <span>{display}</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 ml-2 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-[8500] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="py-4 text-center"><Loader className="animate-spin h-4 w-4 text-brand mx-auto" /></div>
          ) : (
            <div className="max-h-60 overflow-y-auto">
              {/* Default option */}
              <button
                type="button"
                onClick={() => { onChange('', undefined); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors cursor-pointer flex items-center justify-between border-b border-slate-100 ${!value ? 'bg-blue-50 text-brand font-bold' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <span>Default Email Address</span>
                {!value && <Check className="h-3.5 w-3.5 text-brand" />}
              </button>

              {/* Connected email addresses */}
              {mailboxes.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1">
                    <Server className="h-3 w-3 text-brand" />
                    <span>Email Addresses</span>
                  </div>
                  {mailboxes.map(m => {
                    const isSelected = value === `mailbox:${m.id}` || value === m.email;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { onChange(`mailbox:${m.id}`, m.id); setOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors cursor-pointer flex items-center justify-between ${isSelected ? 'bg-blue-50 text-brand font-bold' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <span className="truncate">{m.displayName ? `${m.displayName} <${m.email}>` : m.email}</span>
                        {isSelected && <Check className="h-3.5 w-3.5 text-brand shrink-0 ml-2" />}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Verified email addresses */}
              {senders.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1">
                    <Mail className="h-3 w-3 text-slate-500" />
                    <span>Verified Email Addresses</span>
                  </div>
                  {senders.map(s => {
                    const isSelected = value === s.email;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { onChange(s.email, undefined); setOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors cursor-pointer flex items-center justify-between ${isSelected ? 'bg-blue-50 text-brand font-bold' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <span className="truncate">{s.name ? `${s.name} <${s.email}>` : s.email}</span>
                        {isSelected && <Check className="h-3.5 w-3.5 text-brand shrink-0 ml-2" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
